import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { translationPropType } from '@eventbrite/i18n';

import { PROMO_CODE_FORM_NAME } from '../../constants';
import * as constants from '../../containers/ticketSelection/constants';

import {
    PROMO_CODE_OPTIONS_PROP_TYPE,
    PROMO_CODE_HANDLER_PROP_TYPE,
} from './constants';

import PromoCodeForm from './PromoCodeForm';

/**
 * Helper component to render a notice above the ticket list, if necessary.
 * Information that applies to the sales start and/or end dates of all tickets is
 * displayed here.
 *
 * EB-48765: We should move this ticket notice logic to the TicketsSelectionList
 * component in EDS so that it can be reused.
 */
const Notice = ({ status, dateString, shouldHideStatusOnTickets }) => {
    let component = null;

    // When shouldHideStatusOnTickets is true, it means that we want to hide
    // the status on individual tickets and instead show a notice at the top
    // that applies to ALL tickets.
    if (shouldHideStatusOnTickets) {
        const statusInfo = constants.TICKET_SALES_STATUS_MAP[status];
        const classes = classNames(
            'eds-g-cell eds-g-cell-1-1 eds-l-pad-top-4',
            statusInfo.textColor,
        );

        component = (
            <div className={classes} data-spec="tickets-page-notice">
                {statusInfo.getDisplayText(dateString)}
            </div>
        );
    }

    return component;
};

export default class PromoCodeBar extends React.Component {
    static propTypes = {
        /**
         * The page rendering the PromoCodeBar; passed to PromoCodeForm for tracking purposes
         */
        checkoutStep: PropTypes.string,
        /** True if the event is a registration one */
        isRegEvent: PropTypes.bool,

        /**
         * A common sales start or end date for all tickets, if it exists
         */
        salesDateForAllTickets: translationPropType,
        /**
         * Callback provided by consuming component to handle when promo code link gets clicked
         */
        promoCodeFormOptions: PROMO_CODE_OPTIONS_PROP_TYPE,
        promoCodeFormHandlers: PROMO_CODE_HANDLER_PROP_TYPE,
        /**
         * True when the event has a promo code available
         */
        shouldShowPromoCodeForm: PropTypes.bool,
        /**
         * A sales status (sales end soon, sales not started, etc.) that applies to all tickets, if it exists
         */
        statusForAllTickets: PropTypes.oneOf(
            constants.TICKET_SALES_STATUS_PROP_TYPE,
        ),
        /** Number of tickets with promo code applied */
        ticketsWithAppliedCode: PropTypes.number,
    };

    static defaultProps = {
        promoCodeFormOptions: {},
    };

    _getPromoCodeForm() {
        const {
            checkoutStep,
            isRegEvent,
            promoCodeFormHandlers,
            promoCodeFormOptions,
            shouldShowPromoCodeForm,
            ticketsWithAppliedCode,
        } = this.props;

        return shouldShowPromoCodeForm ? (
            <PromoCodeForm
                data-spec="promo-code-form"
                checkoutStep={checkoutStep}
                form={PROMO_CODE_FORM_NAME}
                handlers={promoCodeFormHandlers}
                isRegEvent={isRegEvent}
                onSubmit={promoCodeFormHandlers.onSubmit}
                options={promoCodeFormOptions}
                numPromoCodeTickets={ticketsWithAppliedCode}
            />
        ) : null;
    }

    render() {
        const { statusForAllTickets, salesDateForAllTickets } = this.props;

        const shouldHideStatusOnTickets = !!(
            statusForAllTickets && salesDateForAllTickets
        );

        return (
            <div>
                {this._getPromoCodeForm()}
                <div className="eds-g-grid">
                    <Notice
                        status={statusForAllTickets}
                        dateString={salesDateForAllTickets}
                        shouldHideStatusOnTickets={shouldHideStatusOnTickets}
                    />
                </div>
            </div>
        );
    }
}
